import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import {
  RoadmapStepValidation,
  RoadmapProjectSummary,
  RoadmapDetailData,
  RoadmapProgress,
  PhaseProgress,
  RoadmapFilterOptions
} from '../types/roadmap';
import { syncAutoValidations, fetchAutoValidationContext } from '../utils/roadmapAutoValidation';
import { ROADMAP_WORKFLOW } from '../data/roadmapWorkflow';

interface RoadmapStore {
  projects: RoadmapProjectSummary[];
  currentProject: RoadmapDetailData | null;
  validations: RoadmapStepValidation[];
  progress: RoadmapProgress | null;
  filters: RoadmapFilterOptions;
  isLoading: boolean;
  error: string | null;

  fetchAdvisorProjects: () => Promise<void>;
  fetchProjectRoadmap: (projectId: string) => Promise<void>;
  updateValidation: (
    projectId: string,
    phaseKey: string,
    stepKey: string,
    data: {
      is_manually_validated?: boolean;
      note?: string;
      attachments?: Array<{ filename: string; url: string; document_id?: string }>;
    }
  ) => Promise<void>;
  forceValidation: (projectId: string, phaseKey: string, stepKey: string, force: boolean) => Promise<void>;
  syncProjectAutoValidations: (projectId: string) => Promise<void>;
  setFilters: (filters: Partial<RoadmapFilterOptions>) => void;
  clearFilters: () => void;
}

export const useRoadmapStore = create<RoadmapStore>((set, get) => ({
  projects: [],
  currentProject: null,
  validations: [],
  progress: null,
  filters: {},
  isLoading: false,
  error: null,

  fetchAdvisorProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          status,
          current_status,
          updated_at,
          owner_id,
          owner:users!projects_owner_id_fkey(
            id,
            full_name,
            email
          )
        `)
        .eq('assigned_advisor_id', user.id)
        .order('updated_at', { ascending: false });

      if (projectsError) throw projectsError;

      const summaries: RoadmapProjectSummary[] = await Promise.all(
        (projects || []).map(async (project) => {
          const { data: progressData } = await supabase.rpc('get_roadmap_progress', {
            p_project_id: project.id
          });

          const { data: validations } = await supabase
            .from('roadmap_step_validations')
            .select('*')
            .eq('project_id', project.id);

          const phaseStatuses: { [key: string]: 'not_started' | 'in_progress' | 'completed' } = {};

          for (const phase of ROADMAP_WORKFLOW) {
            const phaseValidations = (validations || []).filter(v => v.phase_key === phase.key);
            const completedSteps = phaseValidations.filter(
              v => v.is_manually_validated || v.is_auto_validated
            ).length;

            if (completedSteps === 0) {
              phaseStatuses[phase.key] = 'not_started';
            } else if (completedSteps === phase.steps.length) {
              phaseStatuses[phase.key] = 'completed';
            } else {
              phaseStatuses[phase.key] = 'in_progress';
            }
          }

          return {
            project_id: project.id,
            project_name: project.name,
            project_status: project.status,
            owner_name: (project.owner as any)?.full_name || 'Inconnu',
            owner_id: project.owner_id,
            progress_percentage: progressData?.progress_percentage || 0,
            last_activity: project.updated_at,
            phase_statuses: phaseStatuses
          };
        })
      );

      set({ projects: summaries, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  fetchProjectRoadmap: async (projectId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select(`
          *,
          owner:users!projects_owner_id_fkey(
            id,
            full_name,
            email,
            organization,
            phone
          )
        `)
        .eq('id', projectId)
        .single();

      if (projectError) throw projectError;

      const { data: validations, error: validationsError } = await supabase
        .from('roadmap_step_validations')
        .select('*')
        .eq('project_id', projectId);

      if (validationsError) throw validationsError;

      const context = await fetchAutoValidationContext(projectId);

      const { data: progressData } = await supabase.rpc('get_roadmap_progress', {
        p_project_id: projectId
      });

      const phaseProgresses: PhaseProgress[] = await Promise.all(
        ROADMAP_WORKFLOW.map(async (phase) => {
          const { data: phaseProgress } = await supabase.rpc('get_phase_progress', {
            p_project_id: projectId,
            p_phase_key: phase.key
          });
          return phaseProgress || {
            project_id: projectId,
            phase_key: phase.key,
            total_steps: 3,
            completed_steps: 0,
            progress_percentage: 0
          };
        })
      );

      const roadmapData: RoadmapDetailData = {
        project: project,
        validations: validations || [],
        autoValidationContext: context,
        progress: progressData || {
          project_id: projectId,
          total_steps: 15,
          completed_steps: 0,
          progress_percentage: 0
        },
        phaseProgresses
      };

      set({
        currentProject: roadmapData,
        validations: validations || [],
        progress: progressData,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  updateValidation: async (projectId, phaseKey, stepKey, data) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: existing } = await supabase
        .from('roadmap_step_validations')
        .select('*')
        .eq('project_id', projectId)
        .eq('phase_key', phaseKey)
        .eq('step_key', stepKey)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('roadmap_step_validations')
          .update({
            ...data,
            validator_id: user.id,
            validation_date: data.is_manually_validated ? new Date().toISOString() : existing.validation_date,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('roadmap_step_validations')
          .insert({
            project_id: projectId,
            phase_key: phaseKey,
            step_key: stepKey,
            ...data,
            validator_id: user.id,
            validation_date: data.is_manually_validated ? new Date().toISOString() : null
          });

        if (error) throw error;
      }

      await get().fetchProjectRoadmap(projectId);
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  forceValidation: async (projectId, phaseKey, stepKey, force) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      await get().updateValidation(projectId, phaseKey, stepKey, {
        is_manually_validated: force
      });
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  syncProjectAutoValidations: async (projectId: string) => {
    try {
      await syncAutoValidations(projectId);
      await get().fetchProjectRoadmap(projectId);
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters }
    }));
  },

  clearFilters: () => {
    set({ filters: {} });
  }
}));
